import {
  Button,
  Card,
  HSpacer,
  InfoTooltip,
  Text,
  TextLink,
  VSpacer,
} from '@/components/DesignSystem';
import { Routes } from '@/constants/Routes';
import {
  buttonHoverStyle,
  cardStyle,
  DashboardDisclaimer,
  DashboardFooter,
  DashboardHeader,
  DashboardProgramInfo,
  DashboardViewButton,
  darkenedSectionStyle,
  FarmerGreeting,
  getBrandColor,
  kpiCardWrapperStyle,
} from '@/pages/CustomerDashboard/DashboardLayout';
import { EnergyProgressSection } from '@/pages/CustomerDashboard/Energy/EnergyProgressSection';
import {
  EnergyTierBenefitsModal,
} from '@/pages/CustomerDashboard/Energy/EnergyTierBenefitsModal';
import { ProgramSwitch } from '@/pages/CustomerDashboard/Energy/ProgramSwitch';
import { getHasTierGraphics, useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { KPICard } from '@/pages/RetailerOverview/PointsOverview/KPICard';
import { ApiEnergyDashboard } from '@api/interfaces/ApiEnergyDashboard';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Box, ButtonBase, Divider, Stack, useTheme } from '@mui/material';
import { formatNumber } from '@shared/utilities';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EnergyCustomerDashboardProps {
  accessToken: string,
  data: ApiEnergyDashboard,
}

export const EnergyCustomerDashboard = ({ accessToken, data }: EnergyCustomerDashboardProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigateToPage = useDashboardNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showTierBenefitsModal, setShowTierBenefitsModal] = useState(false);
  const { currentTier, farmer, program, retailer, tiers } = data;
  const brandColor = getBrandColor(retailer);
  const hasTierGraphics = getHasTierGraphics(tiers.map(({ name }) => name));

  const programDateRange = t(($) => $.customerDashboard.landing.programDateRange, {
    endDate: program.endDate,
    startDate: program.startDate,
  });
  const openPointsActivity = () => (
    navigateToPage(Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY, accessToken)
  );

  const StatusCard = !!currentTier && (
    <>
      <VSpacer size="5" />
      <Card sx={cardStyle} testID="dashboard-status-card">
        <Stack direction="row">
          {hasTierGraphics && (
            <>
              <TierImage hasTierGraphics={hasTierGraphics} tier={currentTier} />
              <HSpacer size="3" />
            </>
          )}
          <Stack>
            <Stack alignItems="center" direction="row" gap="4px">
              <Text category="overline">
                {t(($) => $.customers.table.filters.currentStatus).toUpperCase()}
              </Text>
              <InfoTooltip
                backgroundColor={theme.palette.background.paper}
                boundaryRef={containerRef}
                iconColor="#525252"
                openOnClick={true}
              >
                {t(($) => $.customerDashboard.energy.statusTooltip)}
              </InfoTooltip>
            </Stack>
            <VSpacer size="3" />
            <Text fontSize="28px" fontWeight={500} testID="energy-dashboard-tier">
              {currentTier.name}
            </Text>
          </Stack>
        </Stack>
        <VSpacer size="4" />
        <Stack data-testid="energy-status-behaviors" gap="4px">
          <Text category="body-medium">
            {t(($) => $.customerDashboard.energy.behaviorsSummary, {
              completed: data.completedCount,
              total: data.behaviors.length,
            })}
          </Text>
          <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
            {data.nextTier
              ? t(($) => $.customerDashboard.energy.nextTierSummary, {
                remaining: t(($) => $.customerDashboard.energy.behaviorsRemaining, {
                  count: data.behaviorsRemainingForNextTier,
                }),
                tierName: data.nextTier.name,
              })
              : t(($) => $.customerDashboard.energy.topTier)}
          </Text>
          <TextLink
            category="label-medium"
            onClick={() => document.querySelector('[data-testid="energy-progress"]')
              ?.scrollIntoView({ behavior: 'smooth' })}
            sx={{ color: brandColor }}
            testID="energy-status-see-checklist"
          >
            {t(($) => $.customerDashboard.energy.seeChecklist)}
          </TextLink>
        </Stack>
        <VSpacer size="4" />
        <Button
          color="inherit"
          fullWidth
          onClick={() => setShowTierBenefitsModal(true)}
          sx={{ borderColor: '#D9D9D9', p: '10px 16px', ...buttonHoverStyle }}
          testID="dashboard-status-card-benefits-button"
          variant="outlined"
        >
          {t(($) => $.customerDashboard.tierBenefits.title, { tierName: currentTier.name })}
        </Button>
      </Card>
    </>
  );

  const NavigationCard = ({ label, onClick, testID, trailing }: {
    label: string,
    onClick: () => void,
    testID: string,
    trailing?: string,
  }) => (
    <ButtonBase onClick={onClick} sx={{ width: '100%' }}>
      <Card sx={[cardStyle, { paddingRight: '24px', width: '100%' }]} testID={testID}>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Text fontSize="18px" fontWeight={400}>{label}</Text>
          <Stack alignItems="center" direction="row" textAlign="right">
            {!!trailing && <Text category="label-medium">{trailing}</Text>}
            <ChevronRight sx={{ color: '#525252' }} />
          </Stack>
        </Stack>
      </Card>
    </ButtonBase>
  );

  return (
    <>
      <Stack
        bgcolor="white"
        data-testid="energy-customer-dashboard"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
        ref={containerRef}
      >
        <DashboardHeader retailer={retailer} />
        <DashboardProgramInfo
          brandColor={brandColor}
          dateRange={programDateRange}
          name={program.name}
        />
        <ProgramSwitch memberships={data.memberships} value="energy" />
        <Divider />
        <Stack p="16px" sx={darkenedSectionStyle}>
          <FarmerGreeting farmer={farmer} />
          <VSpacer size="5" />
          <Stack direction="row" gap="16px" justifyContent="space-between">
            <Box sx={kpiCardWrapperStyle}>
              <KPICard
                boundaryRef={containerRef}
                iconColor="rgba(12, 0, 0, 0.68)"
                label={t(($) => $.customers.table.filters.pointsAvailable)}
                openTooltipOnClick={true}
                sublabel={t(($) => $.customerDashboard.energy.pointsValue, {
                  value: data.points.valueDollars,
                })}
                testID="kpi-card-points-available"
                tooltipText={t(($) => $.customerDashboard.energy.pointsTooltip)}
                valueText={formatNumber(data.points.available, i18n)}
                variant="centered"
              />
            </Box>
          </Stack>
          {StatusCard}
          <VSpacer size="5" />
          <NavigationCard
            label={t(($) => $.customerDashboard.wallet.title)}
            onClick={openPointsActivity}
            testID="wallet-card"
            trailing={t(($) => $.wallet.availablePoints, { points: data.points.available })}
          />
          <VSpacer size="5" />
          <NavigationCard
            label={t(($) => $.customerDashboard.benefits.title)}
            onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_BENEFITS, accessToken)}
            testID="dashboard-benefits-card"
          />
          <VSpacer size="4" />
        </Stack>
        <Divider />
        <EnergyProgressSection data={data} />
        <VSpacer size="8" />
        <Stack p="16px">
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Text category="headline-medium">
              {t(($) => $.customerDashboard.pointsActivity.title)}
            </Text>
            <DashboardViewButton
              brandColor={brandColor}
              onClick={openPointsActivity}
              testID="dashboard-points-activity-button"
            />
          </Stack>
        </Stack>
        <Divider />
        <DashboardDisclaimer />
        <VSpacer size="3" />
        <Divider />
        <DashboardFooter brandColor={brandColor} />
      </Stack>
      {showTierBenefitsModal && (
        <EnergyTierBenefitsModal
          brandColor={brandColor}
          currentRank={currentTier?.rank ?? null}
          onClose={() => setShowTierBenefitsModal(false)}
          tiers={tiers}
        />
      )}
    </>
  );
};
